/* Animated RealU-Score reveal: GPA + habit tiles collide, then the combined
   score emerges. StrictMode-safe (rAF + timeout are cancelled on cleanup).
   ScoreStep is the registry entry that feeds it the live score from context. */
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../icons';
import { habitTotal, scoreBlurb } from '../data';
import { useSurvey } from '../SurveyContext';

// Must match the CSS animation timing (see survey-next.css .intake-score-emerge).
const COLLISION_END = 1050; // ms before score count-up begins
const COUNT_DUR = 750;      // ms for the count-up

export function ScoreReveal({
  score,
  off,
  gpa,
  habitScore,
}: {
  score: number;
  off: number;
  gpa: number;
  habitScore: number;
}) {
  const numRef = useRef<HTMLSpanElement>(null);
  const offRef = useRef<HTMLSpanElement>(null);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const numEl = numRef.current;
    const offEl = offRef.current;
    if (!numEl || !offEl) return;

    numEl.textContent = '0.00';
    offEl.textContent = '0';

    const reduce =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      numEl.textContent = score.toFixed(2);
      offEl.textContent = String(off);
      return;
    }

    let raf = 0;
    const timeout = setTimeout(() => {
      const t0 = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - t0) / COUNT_DUR);
        const e = 1 - Math.pow(1 - p, 3);
        numEl.textContent = (score * e).toFixed(2);
        offEl.textContent = String(Math.round(off * e));
        if (p < 1) {
          raf = requestAnimationFrame(tick);
        } else {
          numEl.textContent = score.toFixed(2);
          offEl.textContent = String(off);
        }
      };
      raf = requestAnimationFrame(tick);
    }, COLLISION_END);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(raf);
    };
  }, [score, off, animKey]);

  return (
    <>
      <div className="intake-reveal">
        <div className="intake-collision-zone" key={animKey}>
          <div className="intake-collider intake-collider--gpa" aria-hidden="true">
            <strong>GPA</strong>
            <span className="intake-collider__val">{gpa.toFixed(2)}</span>
          </div>
          <div className="intake-collider intake-collider--habit" aria-hidden="true">
            <strong>Effort</strong>
            <span className="intake-collider__val">{habitScore.toFixed(2)}</span>
          </div>
          <div className="intake-big intake-big--emerge">
            <span className="intake-num" ref={numRef}>
              0.00
            </span>
          </div>
        </div>
        <div className="intake-offers intake-offers--delayed" key={`off-${animKey}`}>
          <Icon name="trophy" size={19} /> <span className="intake-offnum" ref={offRef}>0</span>
          &nbsp;job offers unlocked
        </div>
        <p className="intake-blurb">{scoreBlurb(score)}</p>
      </div>
    </>
  );
}

export function ScoreStep() {
  const surveyFlow = useSurvey();
  const gpa = typeof surveyFlow.answers.gpa === 'number' ? surveyFlow.answers.gpa : 0;
  const hs = habitTotal(surveyFlow.answers.habits);
  return (
    <ScoreReveal score={surveyFlow.score} off={surveyFlow.offers} gpa={gpa} habitScore={hs} />
  );
}
